import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Tooltip,
  Box,
  Typography,
  Alert
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import { detectTradingResponse } from '../services/tradingAnalyzer';
import { TradingLevels } from '../types/trading';
import PineScriptModal from './PineScriptModal';
import { useAppDispatch, useAppSelector } from '../store';
import {
  analyzeTradingResponse,
  selectActiveTradingAnalysis,
  selectIsTradingAnalysisActive,
  selectTradingUIState,
  setPineScriptModalOpen,
} from '../store';

interface PineScriptButtonProps {
  aiResponseText: string;
  assetName?: string;
}

const PineScriptButton: React.FC<PineScriptButtonProps> = ({ 
  aiResponseText, 
  assetName = 'Trading Asset' 
}) => {
  const dispatch = useAppDispatch();
  const activeAnalysis = useAppSelector(selectActiveTradingAnalysis);
  const isAnalysisActive = useAppSelector(selectIsTradingAnalysisActive);
  const uiState = useAppSelector(selectTradingUIState);
  const [error, setError] = useState<string | null>(null);

  // Detect if this AI response contains trading analysis
  const isTradingResponse = detectTradingResponse(aiResponseText);

  // Auto-detect asset name from AI response
  const detectAssetName = (text: string): string => {
    const patterns = [
      // Common patterns for asset identification
      /(?:for |analysis (?:of )?|chart (?:of )?)(ETH|BTC|SPY|QQQ|AAPL|TSLA|MSFT|GOOGL|AMZN|NVDA|META|NFLX)/gi,
      /(Ethereum|Bitcoin|Tesla|Apple|Microsoft|Google|Amazon|Nvidia|Meta|Netflix)/gi,
      /(ETH\/USD|BTC\/USD|SPY|QQQ)/gi,
      /\b([A-Z]{2,5})\b.*?(?:chart|analysis|levels|resistance|support)/gi
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        const detected = match[1] || match[0];
        // Map common variations
        const assetMap: { [key: string]: string } = {
          'ETH': 'Ethereum (ETH)',
          'Ethereum': 'Ethereum (ETH)', 
          'BTC': 'Bitcoin (BTC)',
          'Bitcoin': 'Bitcoin (BTC)',
          'SPY': 'SPY (S&P 500 ETF)',
          'QQQ': 'QQQ (NASDAQ ETF)',
          'AAPL': 'Apple (AAPL)',
          'TSLA': 'Tesla (TSLA)',
          'Tesla': 'Tesla (TSLA)',
          'MSFT': 'Microsoft (MSFT)',
          'Microsoft': 'Microsoft (MSFT)',
          'NFLX': 'Netflix (NFLX)',
          'Netflix': 'Netflix (NFLX)',
          'NVDA': 'Nvidia (NVDA)',
          'Nvidia': 'Nvidia (NVDA)'
        };
        return assetMap[detected] || detected.toUpperCase();
      }
    }
    return assetName;
  };

  const detectedAssetName = detectAssetName(aiResponseText);

  const handleGeneratePineScript = async () => {
    setError(null);

    try {
      await dispatch(analyzeTradingResponse({
        aiResponseText,
        assetName: detectedAssetName,
      })).unwrap();
    } catch (err: any) {
      console.error('Pine Script generation error:', err);
      setError(err.message || 'Failed to generate Pine Script');
    }
  };

  // Don't render if this isn't a trading response
  if (!isTradingResponse) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(error || activeAnalysis?.error) && (
          <Alert 
            severity="error" 
            onClose={() => {
              setError(null);
              // Note: Redux errors are cleared when starting new analysis
            }} 
            sx={{ fontSize: '0.875rem' }}
          >
            {error || activeAnalysis?.error}
          </Alert>
        )}
        
        {activeAnalysis?.warnings && activeAnalysis.warnings.length > 0 && (
          <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
            {activeAnalysis.warnings.join(', ')}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Generate Pine Script for TradingView" placement="top">
            <Button
              variant="contained"
              color="primary"
              startIcon={isAnalysisActive ? <CircularProgress size={16} color="inherit" /> : <CodeIcon />}
              onClick={handleGeneratePineScript}
              disabled={isAnalysisActive}
              size="medium"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 1,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976D2 30%, #1EAEDB 90%)',
                }
              }}
            >
              {isAnalysisActive ? 'Generating...' : 'Generate Pine Script'}
            </Button>
          </Tooltip>
          
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Create TradingView indicator from this analysis
          </Typography>
        </Box>
      </Box>

      <PineScriptModal
        open={uiState.pineScriptModalOpen}
        onClose={() => dispatch(setPineScriptModalOpen(false))}
        pineScript={activeAnalysis?.results?.pineScriptResult?.script || ''}
        tradingLevels={activeAnalysis?.results?.tradingLevels || null}
        assetName={activeAnalysis?.assetName || detectedAssetName}
      />
    </>
  );
};

export default PineScriptButton;